import { useTranslation } from "react-i18next";

export default function Footer() {
  const { t } = useTranslation();

  const scrollToSection = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-foreground-950 border-t border-white/5">
      <div className="px-6 lg:px-12 xl:px-20 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20technology%20logo%20mark%2C%20interlocking%20hexagonal%20shapes%20forming%20a%20sleek%20minimalist%20emblem%2C%20warm%20terracotta%20copper%20gradient%20on%20white%20background%2C%20clean%20modern%20corporate%20identity%20design%2C%20flat%20vector%20style%2C%20sharp%20precise%20geometric%20lines%2C%20premium%20tech%20branding&width=80&height=80&seq=emporris-brand-mark-v2&orientation=squarish"
                alt="Emporris"
                className="h-7 w-7 object-contain flex-shrink-0 brightness-0 invert"
              />
              <span className="text-lg font-bold text-white tracking-tight">
                Emporris
              </span>
            </div>
            <p className="text-sm text-white/35 leading-relaxed mb-6 max-w-xs">
              {t("footer.about")}
            </p>
            <div className="flex items-center gap-2.5">
              {[
                { icon: "ri-linkedin-fill", href: "#" },
                { icon: "ri-twitter-x-fill", href: "#" },
                { icon: "ri-instagram-line", href: "#" },
                { icon: "ri-facebook-fill", href: "#" },
              ].map((social) => (
                <a
                  key={social.icon}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center transition-colors duration-200 cursor-pointer"
                  aria-label={social.icon}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${social.icon} text-white/50 text-sm`} />
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-5">
              {t("footer.quickLinks")}
            </h4>
            <ul className="space-y-3">
              {[
                { label: "Home", href: "#home" },
                { label: "Services", href: "#services" },
                { label: "Solutions", href: "#solutions" },
                { label: "About", href: "#why" },
                { label: "Contact", href: "#contact" },
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className="text-sm text-white/35 hover:text-white/70 transition-colors duration-200 cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <h4 className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-5">
              {t("footer.services")}
            </h4>
            <ul className="space-y-3">
              {[
                "CRM & ERP Solutions",
                "AI & Intelligent Automation",
                "Custom Software Development",
                "Mobile App Development",
                "Website Development",
              ].map((service) => (
                <li key={service}>
                  <span className="text-sm text-white/35">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h4 className="text-xs font-semibold text-white/30 uppercase tracking-widest mb-5">
              {t("footer.contact")}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-white/25 text-sm" />
                </div>
                <span className="text-sm text-white/35 leading-relaxed">
                  C Tower, 10th Floor, Noida Sector 62, UP 201309, India
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-phone-line text-white/25 text-sm" />
                </div>
                <span className="text-sm text-white/35">+91 6262623452</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-mail-line text-white/25 text-sm" />
                </div>
                <span className="text-sm text-white/35">emporris.solutions@outlook.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-14 pt-7 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/20">
            &copy; {new Date().getFullYear()} {t("footer.copyright")}
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-xs text-white/20 hover:text-white/40 transition-colors cursor-pointer">
              Privacy Policy
            </a>
            <a href="#" className="text-xs text-white/20 hover:text-white/40 transition-colors cursor-pointer">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}